// Single source of truth for the menu brands. The admin tabs, the item modals
// and the public endpoints are all derived from this list.
//
// `variant` is the react-bootstrap colour of the admin tab.
// `langs` are the translation columns the admin edits for the brand (the
// server accepts name_<lang> / description_<lang> for each of them).
// `extraFields` render generically in the item modals as flat body fields.
export const BRANDS = [
  { key: 'esas', label: 'Pizza Inn', adminPrefix: 'esas', publicApi: '/menu', route: '/menu', langs: ['az', 'en', 'ru'], variant: 'primary' },
  { key: 'mokaland', label: 'Moka Land', adminPrefix: 'mokaland', publicApi: '/mokaland-menu', route: '/mokaland', langs: ['az', 'en', 'ru'], variant: 'warning',
    nav: { key: 'nav.mokaland', fallback: 'Moka Land' } },
  { key: 'qisparki', label: 'Garden', adminPrefix: 'qisparki', publicApi: '/qisparki-menu', route: '/menu/qis-parki', langs: ['az', 'en', 'ru'], variant: 'info',
    nav: { key: 'nav.qisParki', fallback: 'Garden' },
    // Public page (BrandMenu). The eyebrow/title/caption keys predate the registry.
    seo: { titleKey: 'brands.qisparki.seoTitle', descriptionKey: 'brands.qisparki.seoDescription', keywords: 'pizza inn garden, pizza menu baku, garden restaurant baku' },
    eyebrowKey: 'menu.gardenEyebrow', titleKey: 'menu.gardenTitle', captionKey: 'qisparki.caption',
    currency: 'AZN', analyticsLabel: 'qisparki' },
  { key: 'all', label: 'ALL', adminPrefix: 'all', publicApi: '/all-menu', route: '/menu/all-eat', langs: ['tr', 'en', 'az', 'ru'], variant: 'success',
    nav: { fallback: 'ALL — İstanbul' } },
  {
    key: 'sedef', label: 'Sədəf', adminPrefix: 'sedef', publicApi: '/sedef-menu', route: '/menu/sedef',
    langs: ['az', 'en', 'ru', 'tr'], variant: 'secondary',
    nav: { fallback: 'Sədəf' },
    seo: { titleKey: 'brands.sedef.seoTitle', descriptionKey: 'brands.sedef.seoDescription', keywords: 'sədəf, sedef, sədəf restoran bakı' },
    eyebrowKey: 'brands.sedef.eyebrow', titleKey: 'brands.sedef.title', captionKey: 'brands.sedef.caption',
    serviceChargeKey: 'brands.sedef.serviceCharge',
    currency: 'AZN', analyticsLabel: 'sedef', themeClass: 'theme-sedef',
    // The printed menu's identity (BrandMenu hooks; styles/sedef.css).
    // The wordmark was traced from the cover. The byline is a brand name, so
    // it is not translated; the owner wants it spelled "Pizza Inn".
    logo: { src: '/brands/sedef/logo.svg', width: 450, height: 170 },
    byline: 'By Pizza Inn',
    // Section banners, keyed by category id. The seed makes ids from the
    // section's az name: cat-<slug>, with ə→e ı→i ö→o ü→u ç→c ş→s ğ→g. Names
    // are translated and ambiguous ("Soyuq içkilər" is not a cold appetizer),
    // so names are never used for matching. The photographs come from the
    // print, with no text or ornaments. An unlisted section gets no banner.
    sectionBanners: Object.fromEntries([
      ['cat-seher-yemeyi', 'breakfast'],
      ['cat-soyuq-qelyanaltilar', 'cold'],
      ['cat-kabablar', 'kebab'],
      ['cat-plovlar', 'plov'],
    ].map(([id, name]) => [id, {
      src: `/brands/sedef/banner-${name}-1200.jpg`,
      srcSet: `/brands/sedef/banner-${name}-600.jpg 600w, /brands/sedef/banner-${name}-1200.jpg 1200w`,
    }])),
    // Section names render as h2, between the h1 and the h3 dish names.
    sectionHeadingLevel: 2,
    // Each section opens with a swipeable strip of its photographed dishes;
    // every dish below is the same text row (a camera mark flags a photo).
    photoLayout: { kind: 'strip', labelKey: 'brands.sedef.photos', markKey: 'brands.sedef.hasPhoto' },
    extraFields: [
      { name: 'size_az', label: 'Size (az)' },
      { name: 'size_en', label: 'Size (en)' },
      { name: 'wolvox_id', label: 'Wolvox ID', type: 'number' },
    ],
  },
];

// Brands that get a link in the header and footer (Pizza Inn's own /menu is a
// fixed entry there). `nav.key` is an i18n key; `nav.fallback` is the default
// text and, where there is no key, the label itself.
export const navBrands = ['qisparki', 'all', 'sedef', 'mokaland']
  .map((key) => BRANDS.find((b) => b.key === key));

// The text of a nav brand's link. The header and the home page's "other rooms"
// both build their lists from navBrands, so they must label them the same way.
export const navLabel = (brand, t) =>
  (brand.nav.key ? t(brand.nav.key, brand.nav.fallback) : brand.nav.fallback);

export const getBrand = (key) => BRANDS.find((b) => b.key === key);

// Pizza Inn's own link in the header and footer. It used to read "Menu", which
// on Sədəf's page looked like Sədəf's menu. A brand name, so not translated.
export const PIZZA_INN_LABEL = getBrand('esas').label;

// The nav venue a path (without its /:lang prefix) belongs to, or undefined for
// Pizza Inn's own pages. Only the explicit nav routes count: /menu/:id is a
// Pizza Inn dish, not a venue. Should one route ever nest in another, the
// longest wins.
export const brandForPath = (path) =>
  navBrands
    .filter((b) => path === b.route || path.startsWith(`${b.route}/`))
    .sort((a, b) => b.route.length - a.route.length)[0];

export const brandEndpoints = (b) => ({
  categories: `${b.publicApi}/categories`,
  items: `${b.publicApi}/items`,
  featured: `${b.publicApi}/featured`,
  search: `${b.publicApi}/search`,
});

export const LANG_LABELS = { az: 'Azərbaycanca', en: 'English', ru: 'Русский', tr: 'Türkçe' };
export const LANG_PLACEHOLDERS = {
  az: ['məs. Pepperoni Pizza', 'Məhsul haqqında qısa məlumat...'],
  en: ['Name in English', 'Description in English'],
  ru: ['Название на русском', 'Описание на русском'],
  tr: ['Türkçe ad', 'Türkçe açıklama'],
};
